// Chọn kỳ Ngày / Tháng / Năm — Tổng quan mọi cấp, Tài chính NPP. Mã kỳ theo common/date-range:
// "2026-11-01" một ngày · "2026-11" một tháng · "2026" một năm. Không chọn được kỳ chưa tới, không trước năm FIRST_PERIOD_YEAR.
//   · PERIOD_UNITS   — nhãn cho nhóm nút Ngày · Tháng · Năm
//   · PeriodStepper  — ‹ [lịch / tháng / năm] › lùi, tiến một kỳ

import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FIRST_PERIOD_YEAR, clampPeriod, currentPeriod, isDateKey, periodLabel, periodUnitOf, shiftPeriod, type PeriodUnit } from '@shoptrack/common/date-range'

export const PERIOD_UNITS: ReadonlyArray<readonly [PeriodUnit, string]> = [
  ['day', 'Ngày'],
  ['month', 'Tháng'],
  ['year', 'Năm'],
]

const STEP_LABEL: Record<PeriodUnit, [string, string]> = {
  day: ['Ngày trước', 'Ngày sau'],
  month: ['Tháng trước', 'Tháng sau'],
  year: ['Năm trước', 'Năm sau'],
}

/** Mọi kỳ chọn được của loại `unit`, mới nhất trước (ô chọn tháng / năm). */
function periodOptions(unit: 'month' | 'year'): string[] {
  const out: string[] = []
  for (let k = currentPeriod(unit); periodUnitOf(k) === unit && clampPeriod(k, unit) === k; k = shiftPeriod(k, -1)) out.push(k)
  return out
}

// Điện thoại: vùng bấm 44px; máy tính gọn cùng cỡ nhóm nút bên cạnh
const STEP_BTN =
  'motion-state grid size-11 shrink-0 place-items-center rounded-lg border border-rule bg-raise text-inkSoft hover:text-ink disabled:pointer-events-none disabled:opacity-35 desktop:size-8'
const FIELD =
  'h-11 min-w-0 flex-1 rounded-lg border border-rule bg-raise px-2.5 text-base tabular-nums text-ink desktop:h-8 desktop:w-36 desktop:flex-none desktop:text-xs focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/20'

/**
 * ‹ [kỳ] ›. Ô lịch của kỳ Ngày: gõ tay thì năm đi qua 0002, 0020… — chỉ áp dụng ngày hợp lệ trong khoảng chọn được,
 * sau khi dừng tay 0,5 giây (như ô Từ – đến); rời ô mà ngày không dùng được thì trả về ngày đang xem.
 */
export function PeriodStepper({ value, onChange, className }: { value: string; onChange: (key: string) => void; className?: string }) {
  const unit = periodUnitOf(value) ?? 'month'
  const cur = currentPeriod(unit)
  const go = (k: string) => {
    const next = clampPeriod(k, unit)
    if (next !== value) onChange(next)
  }
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  const usable = (k: string) => isDateKey(k) && clampPeriod(k, 'day') === k
  useEffect(() => {
    if (unit !== 'day' || draft === value || !usable(draft)) return
    const t = setTimeout(() => onChange(draft), 500)
    return () => clearTimeout(t)
  }, [draft]) // eslint-disable-line react-hooks/exhaustive-deps
  const [prevLabel, nextLabel] = STEP_LABEL[unit]
  return (
    <div className={cn('flex min-w-0 items-center gap-1.5', className)}>
      <button type="button" className={STEP_BTN} aria-label={prevLabel} disabled={clampPeriod(shiftPeriod(value, -1), unit) === value} onClick={() => go(shiftPeriod(value, -1))}>
        <ChevronLeft className="size-4" aria-hidden />
      </button>
      {unit === 'day' ? (
        <input
          type="date"
          aria-label="Ngày"
          value={draft}
          min={`${FIRST_PERIOD_YEAR}-01-01`}
          max={cur}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => !usable(draft) && setDraft(value)}
          className={FIELD}
        />
      ) : (
        <select aria-label={unit === 'month' ? 'Tháng' : 'Năm'} value={value} onChange={(e) => go(e.target.value)} className={FIELD}>
          {periodOptions(unit).map((k) => (
            <option key={k} value={k}>
              {periodLabel(k)}
            </option>
          ))}
        </select>
      )}
      <button type="button" className={STEP_BTN} aria-label={nextLabel} disabled={value >= cur} onClick={() => go(shiftPeriod(value, 1))}>
        <ChevronRight className="size-4" aria-hidden />
      </button>
    </div>
  )
}
